import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { numberSeparator } from '../../../helpers/formats';
import {
  GroupHead,
  GroupLabel,
  GroupLink,
  SuggestionItem,
  SuggestionItemCount,
  SuggestionItemLabel,
  SuggestionsListWrapper,
} from './wrappers';

type Suggestion = {
  path: string;
  label: string;
  hitCount?: number;
};

export interface SuggestionGroup {
  suggestions: Suggestion[];
  label?: string;
  linkLabel?: string;
  linkPath?: string;
}

interface ISuggestionList {
  groups?: SuggestionGroup[];
  focusedIndex: number | null;
  onSetFocusedIndex: (index: number | null) => void;
  onSuggestionClick?: () => void;
}

const SuggestionsList = ({
  groups,
  focusedIndex,
  onSetFocusedIndex,
  onSuggestionClick,
}: ISuggestionList) => {
  const navigate = useNavigate();
  const _suggestions = useMemo(
    () =>
      (groups || []).reduce((prev, curr) => {
        prev.push(...curr.suggestions);
        return prev;
      }, [] as Suggestion[]),
    [groups],
  );

  const onSuggestionMouseOver = useCallback(() => {
    onSetFocusedIndex(null);
  }, [onSetFocusedIndex]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!_suggestions?.length) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          onSetFocusedIndex(
            focusedIndex !== null && focusedIndex >= 1 ? focusedIndex - 1 : _suggestions.length - 1,
          );
          break;

        case 'ArrowDown':
          e.preventDefault();
          onSetFocusedIndex(
            focusedIndex !== null && focusedIndex < _suggestions.length - 1 ? focusedIndex + 1 : 0,
          );
          break;

        case 'Enter':
          if (focusedIndex !== null && _suggestions[focusedIndex]) {
            if (onSuggestionClick) {
              onSuggestionClick();
            }
            navigate(_suggestions[focusedIndex].path);
          }
          break;

        default:
          onSetFocusedIndex(null);
          break;
      }
    },
    [_suggestions, focusedIndex, onSetFocusedIndex, onSuggestionClick],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const suggestionItems = useMemo(() => {
    let accumulatedIndex = 0;

    return groups?.map(({ label, linkLabel, linkPath, suggestions }, groupIndex) => {
      const groupItems = suggestions.map((suggestion, i) => {
        const index = accumulatedIndex + i;
        return (
          <SuggestionItem
            key={`${suggestion.path}-${index}`}
            to={suggestion.path}
            $focused={focusedIndex === index}
            data-index={index}
            onClick={onSuggestionClick}
            onMouseOver={onSuggestionMouseOver}
            title={suggestion.label}
          >
            {suggestion.hitCount !== undefined ? (
              <>
                <SuggestionItemLabel>{suggestion.label}</SuggestionItemLabel>
                <SuggestionItemCount>{numberSeparator(suggestion.hitCount)}</SuggestionItemCount>
              </>
            ) : (
              suggestion.label
            )}
          </SuggestionItem>
        );
      });

      accumulatedIndex += suggestions.length;

      return (
        <React.Fragment key={`suggestiongroup-${groupIndex}`}>
          {label && (
            <GroupHead>
              <GroupLabel>{label}</GroupLabel>
              {linkLabel && linkPath && (
                <GroupLink to={linkPath} title={linkLabel}>
                  {linkLabel}
                </GroupLink>
              )}
            </GroupHead>
          )}
          {groupItems}
        </React.Fragment>
      );
    });
  }, [groups, focusedIndex, onSuggestionClick, onSuggestionMouseOver]);

  return <SuggestionsListWrapper>{suggestionItems}</SuggestionsListWrapper>;
};

export default SuggestionsList;
